import React from "react";
import "../../../style/skeleton.css";

/**
 * SkeletonBox - Base skeleton placeholder component
 * 
 * Non-animated static gray block for TV skeleton loaders.
 * Matches Flutter's SkeletonContainer widget.
 * 
 * @param {number|string} width - Width in pixels or CSS value
 * @param {number|string} height - Height in pixels or CSS value  
 * @param {number|string} borderRadius - Border radius (default: 8px)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 * @param {boolean} circle - If true, renders as circle
 */
function SkeletonBox({
  width,
  height,
  borderRadius,
  className = "",
  style = {},
  circle = false,
}) {
  var boxStyle = {
    backgroundColor: "#262626",
    display: "block",
  };

  // Handle width
  if (width !== undefined) {
    boxStyle.width = typeof width === "number" ? width + "px" : width;
  }

  // Handle height
  if (height !== undefined) {
    boxStyle.height = typeof height === "number" ? height + "px" : height;
  }

  // Handle border radius
  if (circle) {
    boxStyle.borderRadius = "50%";
    boxStyle.WebkitBorderRadius = "50%";
    boxStyle.MozBorderRadius = "50%";
  } else if (borderRadius !== undefined) {
    var radiusValue =
      typeof borderRadius === "number" ? borderRadius + "px" : borderRadius;
    boxStyle.borderRadius = radiusValue;
    boxStyle.WebkitBorderRadius = radiusValue;
    boxStyle.MozBorderRadius = radiusValue;
  } else {
    boxStyle.borderRadius = "8px";
    boxStyle.WebkitBorderRadius = "8px";
    boxStyle.MozBorderRadius = "8px";
  }

  // Merge with custom styles
  var mergedStyle = Object.assign({}, boxStyle, style);

  return (
    <div
      className={"skeleton-box " + className}
      style={mergedStyle}
    />
  );
}

/**
 * SkeletonText - Text line skeleton
 */
function SkeletonText({ width, size = "md", className = "", style = {} }) {
  var heights = {
    sm: 16,
    md: 20,
    lg: 28,
    xl: 36,
  };

  return (
    <SkeletonBox
      width={width}
      height={heights[size] || 20}
      borderRadius={4}
      className={className}
      style={style}
    />
  );
}

/**
 * SkeletonCard - Card placeholder with preset sizes
 */
function SkeletonCard({ type = "portrait", className = "", style = {} }) {
  var dimensions = {
    portrait: { width: 250, height: 350 },
    landscape: { width: 620, height: 350 },
    square: { width: 250, height: 250 },
    artist: { width: 215, height: 215 },
  };

  var dim = dimensions[type] || dimensions.portrait;

  return (
    <SkeletonBox
      width={dim.width}
      height={dim.height}
      borderRadius={type === "artist" ? "50%" : 15}
      className={className}
      style={Object.assign({ flexShrink: 0 }, style)}
    />
  );
}

/**
 * SkeletonButton - Button placeholder
 */
function SkeletonButton({ variant = "primary", className = "", style = {} }) {
  if (variant === "icon") {
    return (
      <SkeletonBox
        width={60}
        height={60}
        circle
        className={className}
        style={style}
      />
    );
  }

  return (
    <SkeletonBox
      width={161}
      height={48}
      borderRadius={47}
      className={className}
      style={style}
    />
  );
}

/**
 * SkeletonChip - Chip/tag placeholder
 */
function SkeletonChip({ width = 100, className = "", style = {} }) {
  return (
    <SkeletonBox
      width={width}
      height={36}
      borderRadius={18}
      className={className}
      style={style}
    />
  );
}

export { SkeletonBox, SkeletonText, SkeletonCard, SkeletonButton, SkeletonChip };
export default SkeletonBox;
